'use client';

import React, { useEffect, useState } from 'react';

interface IBrainLoaderProps {
  onComplete?: () => void;
  duration?: number;
}

export default function IBrainLoader({ onComplete, duration = 2200 }: IBrainLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0);

  const phases = [
    'Initializing EEG Pipeline…',
    'Calibrating Neural Channels…',
    'Syncing Cognitive Engine…',
    'Launching Dashboard…',
  ];

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);
      setPhase(Math.min(Math.floor((pct / 100) * phases.length), phases.length - 1));
      if (pct >= 100) {
        clearInterval(tick);
        setTimeout(() => onComplete?.(), 300);
      }
    }, 30);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0B1628] overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(#1E3A5F 1px, transparent 1px), linear-gradient(90deg, #1E3A5F 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Radial glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
      </div>

      {/* Brainwave lines */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none opacity-20">
        <svg width="800" height="120" viewBox="0 0 800 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 60 Q50 60 80 60 Q100 60 110 20 Q120 -20 130 60 Q140 100 150 60 Q160 20 170 60 Q180 80 200 60 Q250 60 280 60 Q300 60 310 10 Q320 -30 330 60 Q340 110 350 60 Q360 20 370 60 Q380 80 400 60 Q450 60 480 60 Q500 60 510 15 Q520 -25 530 60 Q540 105 550 60 Q560 25 570 60 Q580 80 600 60 Q650 60 680 60 Q700 60 710 20 Q720 -20 730 60 Q740 100 750 60 Q760 20 770 60 Q780 80 800 60"
            stroke="#F97316"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            style={{ animation: 'waveMove 3s linear infinite' }}
          />
          <path
            d="M0 60 Q50 60 80 60 Q100 60 110 35 Q120 10 130 60 Q140 85 150 60 Q160 35 170 60 Q180 70 200 60 Q250 60 280 60 Q300 60 310 30 Q320 5 330 60 Q340 90 350 60 Q360 35 370 60 Q380 70 400 60 Q450 60 480 60 Q500 60 510 32 Q520 8 530 60 Q540 88 550 60 Q560 35 570 60 Q580 70 600 60 Q650 60 680 60 Q700 60 710 35 Q720 10 730 60 Q740 85 750 60 Q760 35 770 60 Q780 70 800 60"
            stroke="#2D5A8E"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            style={{ animation: 'waveMove 4s linear infinite reverse' }}
          />
        </svg>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Logo mark */}
        <div className="relative">
          {/* Outer ring */}
          <div className="w-24 h-24 rounded-full border-2 border-[#1E3A5F] flex items-center justify-center"
            style={{ animation: 'spin 8s linear infinite' }}
          >
            {[0, 60, 120, 180, 240, 300].map((deg) => (
              <div
                key={deg}
                className="absolute w-2 h-2 rounded-full bg-[#F97316]/60"
                style={{
                  transform: `rotate(${deg}deg) translateY(-44px)`,
                }}
              />
            ))}
          </div>
          {/* Inner pulsing ring */}
          <div className="absolute inset-3 rounded-full border border-[#F97316]/30"
            style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
          />
          {/* Center brain icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.5 2C7.01 2 5 4.01 5 6.5c0 .95.3 1.82.8 2.54C4.73 9.7 4 10.77 4 12c0 1.1.45 2.1 1.17 2.83C4.45 15.56 4 16.56 4 17.5 4 19.99 6.01 22 8.5 22c.95 0 1.82-.3 2.54-.8.62.52 1.42.8 2.26.8h.2c.84 0 1.64-.28 2.26-.8.72.5 1.59.8 2.54.8C20.99 22 23 19.99 23 17.5c0-.94-.45-1.94-1.17-2.67C22.55 14.1 23 13.1 23 12c0-1.23-.73-2.3-1.8-2.96.5-.72.8-1.59.8-2.54C22 4.01 19.99 2 17.5 2c-.95 0-1.82.3-2.54.8C14.34 2.3 13.54 2 12.7 2h-.2c-.84 0-1.64.28-2.26.8C9.52 2.3 8.45 2 9.5 2z" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 6v12M9 9l6 6M15 9l-6 6" stroke="#F97316" strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
            </svg>
          </div>
        </div>

        {/* Brand name */}
        <div className="text-center">
          <div className="text-4xl font-bold tracking-tight mb-1">
            <span className="text-[#F97316]">iBrain</span>
            <span className="text-white"> LAB</span>
          </div>
          <div className="text-[13px] text-[#2D5A8E] font-medium tracking-[0.2em] uppercase">
            Cogni Dashboard
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-64 flex flex-col gap-2">
          <div className="h-1 rounded-full bg-[#1E3A5F] overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#EA580C] transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-zinc-500 font-mono">{phases[phase]}</span>
            <span className="text-[11px] text-[#F97316] font-mono tabular-nums">{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Scanning dots */}
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#F97316]"
              style={{
                animation: `bounce 1.2s ease-in-out ${i * 0.15}s infinite`,
                opacity: 0.4,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes waveMove {
          0% { transform: translateX(0); }
          100% { transform: translateX(-400px); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scaleY(0.4); opacity: 0.4; }
          40% { transform: scaleY(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
